/**
 * "Someone has responded" (waiting filter round). The owner: "Really there
 * should just be the ability to filter based on it, and a notification if
 * someone has responded."
 *
 * What the screen can actually see of a response, and nothing more:
 *
 * 1. An ask (data request) Merlin sent to that person was answered. Every
 *    medium an ask goes out on (email, text, Slack, the sales-site widget)
 *    lands its reply as a response on the ask, with who and when
 *    (services/data_requests/ingest.py), so this is the strong signal.
 * 2. That person emailed about the item (notes round, then card 5612eb1c):
 *    the server records a reply from the routee's own address on the
 *    hand-off email's thread, or any mail from them after the send that
 *    names the item (its number, an order / customer ref), as
 *    `route_reply_received` (services/signal_threads/route_replies.py),
 *    and the item carries the newest one as `latest_route_reply` with
 *    `kind` saying which it was.
 * 3. That person did something on the item itself in the portal (a thread
 *    timeline row written by them, with their name), after the item was
 *    marked as waiting on them. Read from the item's history window; rows
 *    missing from it are older ones (the door reads newest first), so a
 *    window that does not hold the newest rows claims nothing
 *    (deliveryView.timelineIsCurrent).
 *
 * What it cannot see, and so never claims: an email from them that names
 * neither the item nor one of its refs, a call, a chat. Those reach Merlin
 * only through the nightly mailbox run, as text in his summaries.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type {
  DoorInvestigationRead,
  DoorRouteReplyRead,
  DoorThreadEventRead,
  DoorThreadRead,
} from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { isRoutedToUser } from '../../components/portal/threadTriageView';
import { routeeNames, sameName, timelineIsCurrent, type TimelineWindow } from './deliveryView';
import { asksForThread, caseForAsk } from './qualityWorkspaceView';
import { waitingOn } from './waitingOnView';

export interface ItemResponse {
  /** Who responded, as the record names them ("Cliff Platt"). */
  who: string;
  /** When (ISO). */
  at: string;
  /** An answered ask, an email from them, or something they did on the
   *  item itself. */
  via: 'ask' | 'email' | 'item';
  /** For an email: a reply on the hand-off's own thread, or mail about the
   *  item that arrived some other way. */
  how?: 'hand_off_reply' | 'about_item';
  /** For mail about the item: the refs that tied it to the item. */
  matchedRefs?: string[];
  /** The ask's question, when via an ask. */
  question?: string;
  requestId?: string;
}

function ms(iso: string | null | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? NaN : t;
}

function newest(list: ItemResponse[]): ItemResponse | null {
  return list.reduce<ItemResponse | null>((best, r) => (!best || ms(r.at) > ms(best.at) ? r : best), null);
}

/** The person an answer on this ask came from: the person it was asked of,
 *  or for a team / company ask, whoever wrote the answer. */
function answeredBy(request: DoorDataRequestRead, responder: string): string {
  // The daily sweep found the answer elsewhere (kanban 3eed0be5): the
  // person asked never replied, so the row must not read as theirs.
  if (responder.startsWith('merlin:')) return 'Merlin';
  return request.audience === 'team' || request.audience === 'company'
    ? responder || request.target_name
    : request.target_name;
}

type Matcher = (candidate: string, who: string) => boolean;

/** A recorded wait names one person: the full name, exact after trimming
 *  and case-folding (card a095e349 — "Cliff Platt" is not "Cliff"). */
export function exactName(a: string, b: string): boolean {
  const norm = (x: string) => x.split(/\s+/).filter(Boolean).join(' ').toLocaleLowerCase();
  return norm(a) !== '' && norm(a) === norm(b);
}

/** The answers on these asks from `who`, after `since` (ms). */
function askAnswersFrom(asks: DoorDataRequestRead[], who: string, since: number, match: Matcher = sameName): ItemResponse[] {
  const out: ItemResponse[] = [];
  for (const r of asks) {
    const responses = r.responses ?? [];
    for (const resp of responses) {
      const by = answeredBy(r, resp.responder_identifier);
      if (!match(by, who) || !(ms(resp.created_at) > since)) continue;
      out.push({ who: by, at: resp.created_at, via: 'ask', question: r.question_title, requestId: r.id });
    }
    // An answered ask whose responses did not come along: its answered time.
    if (responses.length === 0 && r.answered_at && match(r.target_name, who) && ms(r.answered_at) > since) {
      out.push({ who: r.target_name, at: r.answered_at, via: 'ask', question: r.question_title, requestId: r.id });
    }
  }
  return out;
}

/** A recorded hand-off reply as a response (when it came in, by name). */
function replyResponse(reply: DoorRouteReplyRead, fallbackAt: string): ItemResponse {
  return {
    who: reply.from_name,
    at: reply.received_at ?? fallbackAt,
    via: 'email',
    how: reply.kind === 'about_item' ? 'about_item' : 'hand_off_reply',
    matchedRefs: reply.kind === 'about_item' ? [...(reply.matched_refs ?? [])] : undefined,
  };
}

/** Hand-off replies from `who` after `since` (ms): the item's newest one,
 *  and any the loaded history window holds. */
function emailRepliesFrom(
  thread: Partial<Pick<DoorThreadRead, 'latest_route_reply'>>,
  window: TimelineWindow<DoorThreadEventRead> | null | undefined,
  who: string,
  since: number,
  match: Matcher = sameName,
): ItemResponse[] {
  const out: ItemResponse[] = [];
  const latest = thread.latest_route_reply;
  if (latest?.received_at) out.push(replyResponse(latest, latest.received_at));
  for (const e of window?.events ?? []) {
    if (e.event_type === 'route_reply_received' && e.reply) out.push(replyResponse(e.reply, e.created_at));
  }
  return out.filter((r) => match(r.who, who) && ms(r.at) > since);
}

/**
 * Has the person this item waits on responded since it was marked waiting
 * on them? The newest response the screen can see, or null — null means
 * "nothing seen", never "they have not answered".
 *
 * `since` is when the wait started (the record's set_at; for a legacy
 * note, the posture change that wrote it); a response before it is not an
 * answer to the wait. A recorded wait the server already ended as
 * ANSWERED (card a095e349: the person's email reply, ask answer or note,
 * recorded on the item) is that answer. `asks` are the item's own asks;
 * `window` is its loaded history (null when not loaded).
 */
export function responseToWait(
  thread: Pick<DoorThreadRead, 'status' | 'work_state' | 'work_state_note'> &
    Partial<Pick<DoorThreadRead, 'latest_route_reply' | 'work_state_changed_at' | 'wait'>>,
  asks: DoorDataRequestRead[],
  window: TimelineWindow<DoorThreadEventRead> | null | undefined,
): ItemResponse | null {
  const w = waitingOn(thread);
  if (!w) return null;
  if (w.state === 'answered' && thread.wait?.ended_at) {
    const via = thread.wait.ended_via;
    return {
      who: thread.wait.answered_by || w.who,
      at: thread.wait.ended_at,
      via: via === 'email' || via === 'ask' ? via : 'item',
    };
  }
  const since = ms(w.since);
  if (Number.isNaN(since)) return null;
  // A recorded wait is on one person, matched exactly; a legacy note's
  // typed name ("Cliff") keeps the loose rule it always had.
  const match: Matcher = w.source === 'record' ? exactName : sameName;
  const found = [...askAnswersFrom(asks, w.who, since, match), ...emailRepliesFrom(thread, window, w.who, since, match)];
  if (window && timelineIsCurrent(window)) {
    for (const e of window.events) {
      const by = (e.actor_label ?? '').trim();
      if (e.actor_kind !== 'human' || !by || !match(by, w.who) || !(ms(e.created_at) > since)) continue;
      found.push({ who: by, at: e.created_at, via: 'item' });
    }
  }
  return newest(found);
}

/**
 * Did someone the item is with answer the hand-off email of its CURRENT
 * send? The newest recorded reply, when it came in after the item was last
 * sent (routed_at) from a person it is routed to; null otherwise. A reply to
 * an earlier send, or from someone it is no longer with, is not an answer
 * to this one.
 */
export function handOffReply(
  thread: Pick<DoorThreadRead, 'routed_to' | 'routed_at'> & Partial<Pick<DoorThreadRead, 'latest_route_reply'>>,
): ItemResponse | null {
  const reply = thread.latest_route_reply;
  if (!reply?.received_at) return null;
  const sent = ms(thread.routed_at);
  if (Number.isNaN(sent) || !(ms(reply.received_at) > sent)) return null;
  if (!routeeNames(thread.routed_to).some((n) => sameName(n, reply.from_name))) return null;
  return replyResponse(reply, reply.received_at);
}

/** An answer to one of the door's asks that nobody has opened yet. */
export function unseenAnswer(request: DoorDataRequestRead): ItemResponse | null {
  if (request.status !== 'answered' || !request.answer_unseen) return null;
  const last = [...(request.responses ?? [])].sort((a, b) => ms(b.created_at) - ms(a.created_at))[0];
  const at = last?.created_at ?? request.answered_at;
  if (!at) return null;
  return {
    who: answeredBy(request, last?.responder_identifier ?? ''),
    at,
    via: 'ask',
    question: request.question_title,
    requestId: request.id,
  };
}

/** The newest unread answer on these asks (a case's, an item's). */
export function newestUnseenAnswer(asks: DoorDataRequestRead[]): ItemResponse | null {
  return newest(asks.map(unseenAnswer).filter((r): r is ItemResponse => r !== null));
}

/* ---------------------------------------------------------------- */
/* The notices: Triage's line and the tab counts                     */
/* ---------------------------------------------------------------- */

export interface ResponseNotice {
  response: ItemResponse;
  /** Where it landed. */
  kind: 'thread' | 'case' | 'chat';
  /** QT-… / INV-…; null for an ask from a chat with no item. */
  itemNo: string | null;
  /** Path from the door's index (`threads/QT-…`, `cases/INV-…`, `ask?conversation=…`). */
  path: string;
  /** The item was marked as waiting on this person. */
  waited: boolean;
  thread?: DoorThreadRead;
  investigation?: DoorInvestigationRead;
}

/**
 * Everything someone has answered that the reader should hear about, newest
 * first: each of the reader's live items marked as waiting on a person who has since
 * responded, then every answered ask nobody has opened (one notice per ask,
 * skipping the ones a waiting item already speaks for). Closed items and
 * asks on cancelled cases say nothing.
 */
export function responseNotices(
  threads: DoorThreadRead[],
  investigations: DoorInvestigationRead[],
  requests: DoorDataRequestRead[],
  userName: string | null | undefined,
  timelines: Record<string, TimelineWindow<DoorThreadEventRead> | undefined> = {},
): ResponseNotice[] {
  const out: ResponseNotice[] = [];
  const spoken = new Set<string>();
  // Waits are the reader's own: an item sent on to someone else is theirs.
  for (const t of threads.filter((x) => isRoutedToUser(x, userName))) {
    const r = responseToWait(t, asksForThread(requests, t), timelines[t.id] ?? null);
    if (!r) continue;
    if (r.requestId) spoken.add(r.requestId);
    out.push({ response: r, kind: 'thread', itemNo: t.thread_no, path: `threads/${encodeURIComponent(t.thread_no)}`, waited: true, thread: t });
  }
  for (const request of requests) {
    const r = unseenAnswer(request);
    if (!r || spoken.has(request.id)) continue;
    const inv = caseForAsk(request, investigations, threads);
    if (inv) {
      if (inv.status === 'cancelled') continue;
      const birth = threads.find((t) => t.investigation_id === inv.id);
      out.push({ response: r, kind: 'case', itemNo: inv.investigation_no, path: `cases/${encodeURIComponent(inv.investigation_no)}`, waited: false, investigation: inv, thread: birth });
      continue;
    }
    const thread = request.conversation_id
      ? threads.find((t) => t.conversation_id === request.conversation_id)
      : undefined;
    if (thread) {
      if (thread.status === 'completed' || thread.status === 'dismissed') continue;
      out.push({ response: r, kind: 'thread', itemNo: thread.thread_no, path: `threads/${encodeURIComponent(thread.thread_no)}`, waited: false, thread });
    } else if (request.conversation_id) {
      out.push({ response: r, kind: 'chat', itemNo: null, path: `ask?conversation=${encodeURIComponent(request.conversation_id)}`, waited: false });
    }
  }
  return out.sort((a, b) => ms(b.response.at) - ms(a.response.at));
}

/** The response to show on a thread's row: the wait answered, else the
 *  newest unread answer on it. */
export function noticeForThread(notices: ResponseNotice[], thread: Pick<DoorThreadRead, 'id'>): ResponseNotice | null {
  return notices.find((n) => n.kind === 'thread' && n.thread?.id === thread.id) ?? null;
}

/** Same for a case card. */
export function noticeForCase(notices: ResponseNotice[], inv: Pick<DoorInvestigationRead, 'id'>): ResponseNotice | null {
  return notices.find((n) => n.kind === 'case' && n.investigation?.id === inv.id) ?? null;
}

/** "2h ago" / "yesterday" / "Oct 1": when a response landed, for a row. */
export function respondedWhen(at: string, now: Date = new Date()): string {
  const t = ms(at);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.floor((now.getTime() - t) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "replied by email" (on the hand-off's thread) / "wrote about it by email"
 *  (mail that named the item some other way) / "responded" (an ask, the item). */
export function respondedVerb(r: Pick<ItemResponse, 'via' | 'how'>): string {
  if (r.via !== 'email') return 'responded';
  return r.how === 'about_item' ? 'wrote about it by email' : 'replied by email';
}

/** "Cliff Platt responded 2h ago" / "Cliff Platt replied by email 2h ago" /
 *  "John Kirk wrote about it by email Sep 1". */
export function respondedLine(r: ItemResponse, now: Date = new Date()): string {
  return `${r.who} ${respondedVerb(r)} ${respondedWhen(r.at, now)}`;
}
